/* eslint-disable react-native/no-inline-styles */
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { View, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import SociollaText from '@components/sociolla/SociollaText';
import { Loader } from '@components/screen/Layout';
import Image from '@components/global/Image';
import HeaderRow from '@/screens/catalog/components/HeaderRow';
import SearchInputMobile from '@components/global/SearchInputMobile';

import Colors from '@global/colors';
import { isWeb, fullHeight } from '@global/screens';
import { getBrandLetters } from '@/store/resources/actions';

import PropTypes from 'prop-types';

const propTypes = {
  selected: PropTypes.array,
  onSelect: PropTypes.func,
};

const defaultProps = {
  selected: [],
  onSelect: () => {},
};

const BrandFilter = (props) => {
  const { selected = [], onSelect = () => {} } = props;
  const dispatch = useDispatch();
  const scrollRef = useRef();
  const brandLetters = useSelector((state) => state.resources.brandLetters);
  const [keyword, setKeyword] = useState('');
  const [lists, setLists] = useState([]);

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      dispatch(getBrandLetters());
    }
    return () => {
      mounted = false;
    };
  }, [props]);

  useEffect(() => {
    let mounted = true;
    if (mounted && brandLetters?.data?.length) {
      setLists(brandLetters.data);
    }
    return () => {
      mounted = false;
    };
  }, [brandLetters]);

  const goToLetter = (item) => {
    try {
      scrollRef?.current?.scrollTo({
        x: 0,
        y: item.size,
        animated: true,
      });
    } catch (error) {}
  };

  const Alphabet = () => {
    return brandLetters?.alphabets?.length ? (
      <View style={isWeb ? { minHeight: 38 } : {}}>
        <HeaderRow>
          {brandLetters?.alphabets.map((item, idx) => (
            <TouchableOpacity
              activeOpacity={0.8}
              key={`letter-${idx}`}
              style={styles.headerItem}
              onPress={() => goToLetter(item)}>
              <SociollaText style={{ fontSize: isWeb ? 15 : 14 }}>
                {item.label}
              </SociollaText>
            </TouchableOpacity>
          ))}
        </HeaderRow>
      </View>
    ) : (
      false
    );
  };

  const onSelectBrand = (item) => {
    const old = JSON.parse(JSON.stringify(selected));
    const idx = old?.findIndex((a) => a.id == item.id);
    if (idx > -1) {
      old.splice(idx, 1);
    } else {
      old.push({
        id: item.id,
        name: item.name,
      });
    }
    onSelect(old);
  };

  const onRemove = (index) => {
    const old = JSON.parse(JSON.stringify(selected));
    old.splice(index, 1);
    onSelect(old);
  };

  const onSearch = (val) => {
    const getStrName = (name) => {
      return name?.toLowerCase()?.replace(/[^A-Za-z]/g, '');
    };
    const result = brandLetters?.data?.filter((a) => {
      return val ? getStrName(a.name)?.includes(getStrName(val)) : true;
    });
    setLists(result);
  };

  return (
    <View style={styles.wrapper}>
      <SearchInputMobile
        noHorizontal
        searchKey={'name'}
        placeholder={'Search for a brand'}
        value={keyword}
        onSearch={async (value) => {
          setKeyword(value);
          onSearch(value);
        }}
      />
      {selected?.length ? (
        <View style={styles.resultWrapper}>
          {selected.map((item, idx) => (
            <View style={styles.result} key={idx}>
              <SociollaText style={styles.resultText}>{item.name}</SociollaText>
              <TouchableOpacity onPress={() => onRemove(idx)}>
                <Image
                  width={8}
                  height={8}
                  style={styles.remove}
                  svg={true}
                  source={require('@assets/img/icons/close-grey.svg')}
                />
              </TouchableOpacity>
            </View>
          ))}
        </View>
      ) : (
        false
      )}
      <Alphabet />
      <ScrollView
        ref={scrollRef}
        showsHorizontalScrollIndicator={false}
        style={{ maxHeight: isWeb ? 350 : fullHeight - 330 }}>
        {lists?.length ? (
          lists.map((item, idx) => (
            <TouchableOpacity
              activeOpacity={item.size == undefined ? 0.6 : 1}
              style={[styles.brandItem, item.style]}
              onPress={() =>
                item.size == undefined ? onSelectBrand(item) : null
              }
              key={idx}
              nativeId={`alphabet-letter-${idx}`}
              id={`alphabet-letter-${idx}`}>
              {item.size == undefined ? (
                <View
                  style={[
                    styles.box,
                    selected?.findIndex((a) => a.id == item.id) > -1
                      ? styles.boxActive
                      : {},
                  ]}>
                  {selected?.findIndex((a) => a.id == item.id) > -1 ? (
                    <Ionicons name="checkmark" size={12} color={Colors.white} />
                  ) : (
                    false
                  )}
                </View>
              ) : (
                false
              )}
              <SociollaText
                type={item.size != undefined ? 'medium' : ''}
                style={{ fontSize: 14 }}>
                {item.name ? item.name : '-'}
              </SociollaText>
            </TouchableOpacity>
          ))
        ) : (
          <Loader />
        )}
      </ScrollView>
    </View>
  );
};

BrandFilter.propTypes = propTypes;
BrandFilter.defaultProps = defaultProps;

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
  },
  brandItem: {
    width: '100%',
    paddingVertical: 13,
    backgroundColor: Colors.white,
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey0,
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerItem: {
    paddingVertical: isWeb ? 10 : 15,
    paddingHorizontal: isWeb ? 0 : 10,
    ...(isWeb
      ? {
          width: 'calc(100% / 27)',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
        }
      : {}),
  },
  remove: { width: 8, height: 8 },
  resultWrapper: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    borderBottomColor: Colors.grey0,
    borderBottomWidth: 1,
    paddingVertical: 5,
  },
  result: {
    backgroundColor: Colors.blue6,
    paddingHorizontal: 12,
    paddingVertical: 7,
    marginLeft: 5,
    borderRadius: 12,
    marginVertical: 5,
    minHeight: 19,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    maxWidth: '100%',
  },
  resultText: { fontSize: 11, marginRight: 15 },
  box: {
    width: 15,
    height: 15,
    borderRadius: 2,
    backgroundColor: Colors.transparent,
    borderWidth: 1,
    borderColor: Colors.grey0,
    marginRight: 10,
  },
  boxActive: {
    borderColor: Colors.blue0,
    backgroundColor: Colors.blue0,
  },
});

export default React.memo(BrandFilter, (prev, next) => prev == next);
